import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { SymbolType, WallType, FillPattern } from '../../types/floorplannerTypes';
import { FILL_PATTERN_OPTIONS, canonicalizeFillPattern } from '../../utils/fillPatterns';
import { getPatternPreviewDataURL } from '../../utils/patternTextures';
import {
  panelAppearanceRowClassName,
  panelRowControlClassName,
  panelRowLabelClassName,
} from './PropertyPanel/propertyPanelUtils';

interface FillPatternSelectorProps {
  object: SymbolType | WallType;
  currentPattern?: FillPattern;
  isMixed?: boolean;
  onUpdate: (object: any, property: string, value: any) => void;
}

const FillPatternSelector: React.FC<FillPatternSelectorProps> = ({ object, currentPattern, isMixed = false, onUpdate }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, maxHeight: 360 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const dropdownWidth = 220;
    const maxDropdownHeight = 360;
    const minDropdownHeight = 140;
    const viewportPadding = 12;
    const triggerGap = 8;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const shouldOpenUp = spaceBelow < maxDropdownHeight && spaceAbove > spaceBelow;
    const availableHeight = shouldOpenUp ? spaceAbove - triggerGap : spaceBelow - triggerGap;
    const maxHeight = Math.max(minDropdownHeight, Math.min(maxDropdownHeight, availableHeight));
    const top = shouldOpenUp
      ? rect.top - triggerGap - maxHeight
      : rect.bottom + triggerGap;
    const maxLeft = window.innerWidth - viewportPadding - dropdownWidth;

    setDropdownPosition({
      top: Math.max(viewportPadding, Math.min(top, window.innerHeight - viewportPadding - maxHeight)),
      left: Math.max(viewportPadding, Math.min(rect.right - dropdownWidth, maxLeft)),
      maxHeight,
    });
  }, []);

  // Update dropdown position when opened
  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (pattern: FillPattern | undefined) => {
    onUpdate(object, 'fillPattern', pattern);
    setIsOpen(false);
  };

  const resolvedPattern = canonicalizeFillPattern(currentPattern);
  const currentOption = isMixed
    ? { value: undefined, label: t('common.mixed') }
    : FILL_PATTERN_OPTIONS.find(opt => opt.value === resolvedPattern) || FILL_PATTERN_OPTIONS[0];

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      data-fill-pattern-dropdown
      className="fixed bg-white border border-gray-300 rounded shadow-lg z-[9999] w-[220px] max-h-[360px] overflow-y-auto"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        maxHeight: `${dropdownPosition.maxHeight}px`,
      }}
    >
      {FILL_PATTERN_OPTIONS.map((option) => (
        <div
          key={option.value ?? 'none'}
          className={`flex items-center px-2 py-2 cursor-pointer hover:bg-gray-100 first:rounded-t last:rounded-b ${
            !isMixed && option.value === resolvedPattern ? 'bg-indigo-50' : ''
          }`}
          onClick={() => handleSelect(option.value)}
        >
          {option.value ? (
            <img
              src={getPatternPreviewDataURL(option.value)}
              alt={option.label}
              className="w-6 h-6 mr-2 border border-gray-200 rounded"
            />
          ) : (
            <div className="w-6 h-6 mr-2 border border-gray-200 rounded bg-white" />
          )}
          <span className="text-sm">{option.label}</span>
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div className="relative">
      <div className={panelAppearanceRowClassName}>
        <div className={panelRowLabelClassName}>{t('common.pattern')}</div>
        <button
          ref={buttonRef}
          type="button"
          aria-label={t('common.pattern')}
          className={`${panelRowControlClassName} flex items-center justify-between gap-2 text-left hover:border-gray-400`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="truncate">{currentOption.label}</span>
          <svg
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {createPortal(dropdown, document.body)}
    </div>
  );
};

export default FillPatternSelector;
